/**
 * QualityTab — Tasks Reports Phase 1.
 *
 * Surfaces revision rate, avg revisions, first-time approval rate, and
 * reopens for all tasks completed in the window. Includes a per-assignee
 * quality table for drill-down.
 */
import React, { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, CheckCircle2, Repeat, AlertOctagon, BadgeCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const buildParams = (filters) => {
  const p = { from_date: filters.fromDate, to_date: filters.toDate };
  if (filters.assigneeId && filters.assigneeId !== 'all') p.assignee_id = filters.assigneeId;
  if (filters.clientId && filters.clientId !== 'all') p.client_id = filters.clientId;
  if (filters.department && filters.department !== 'all') p.department = filters.department;
  if (filters.taskType && filters.taskType !== 'all') p.task_type = filters.taskType;
  if (filters.subtype && filters.subtype !== 'all') p.subtype = filters.subtype;
  if (filters.priority && filters.priority !== 'all') p.priority = filters.priority;
  if (filters.search) p.search = filters.search;
  return p;
};

const Tile = ({ icon: Icon, label, value, suffix, hint, tone = 'neutral', testid }) => {
  const toneCls = {
    pink: 'text-pink-700', emerald: 'text-emerald-700', amber: 'text-amber-700',
    red: 'text-red-700', blue: 'text-blue-700', neutral: 'text-neutral-800',
  }[tone] || 'text-neutral-800';
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${toneCls}`} />}
      </div>
      <div className={`text-[28px] font-semibold leading-none ${toneCls} tabular-nums`}>
        {value}{suffix && <span className="text-base font-normal text-neutral-400 ml-1">{suffix}</span>}
      </div>
      <div className="text-[11px] text-neutral-400 mt-2">{hint}</div>
    </div>
  );
};

// Color-coded ProgressBar — emerald (good) / amber (warning) / red (bad)
const RateBar = ({ value, inverse = false, threshold = { good: 30, warn: 60 } }) => {
  const tone =
    inverse
      ? value >= 80 ? 'emerald' : value >= 50 ? 'amber' : 'red'
      : value <= threshold.good ? 'emerald' : value <= threshold.warn ? 'amber' : 'red';
  const bg = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' }[tone];
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
        <div className={`h-full ${bg} transition-all`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className="text-[11px] tabular-nums text-neutral-600 min-w-[36px] text-right">{value}%</span>
    </div>
  );
};

const QualityTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = useMemo(() => buildParams(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/admin/reports/tasks/quality`, { headers: auth(), params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(e.response?.data?.detail || 'Failed to load quality metrics'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse h-[110px]" />
        ))}
      </div>
    );
  }

  const t = data?.tiles || {};
  const rows = data?.per_assignee || [];
  const hasData = (t.completed ?? 0) > 0;

  return (
    <div className="space-y-4" data-testid="quality-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={CheckCircle2} label="Completed (window)" value={t.completed ?? 0}
          hint="basis for all rates" tone="emerald" testid="quality-tile-completed"
        />
        <Tile
          icon={RefreshCw} label="Revision rate" value={t.revision_rate ?? 0} suffix="%"
          hint={`${t.revisions ?? 0} revisions / ${t.completed ?? 0} completed`}
          tone={t.revision_rate <= 30 ? 'emerald' : t.revision_rate <= 60 ? 'amber' : 'red'}
          testid="quality-tile-revision-rate"
        />
        <Tile
          icon={Repeat} label="Avg revisions / task" value={t.avg_revisions_per_task ?? 0}
          hint="lower is better"
          tone={t.avg_revisions_per_task <= 0.5 ? 'emerald' : t.avg_revisions_per_task <= 1 ? 'amber' : 'red'}
          testid="quality-tile-avg-revisions"
        />
        <Tile
          icon={BadgeCheck} label="First-time approval" value={t.first_time_approval_rate ?? 0} suffix="%"
          hint="passed without any revision"
          tone={t.first_time_approval_rate >= 70 ? 'emerald' : t.first_time_approval_rate >= 50 ? 'amber' : 'red'}
          testid="quality-tile-fta"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={AlertOctagon} label="Reopens" value={t.reopens ?? 0}
          hint={`${t.reopens_rate ?? 0}% of completed`}
          tone={t.reopens === 0 ? 'emerald' : t.reopens <= 2 ? 'amber' : 'red'}
          testid="quality-tile-reopens"
        />
      </div>

      {/* Per-assignee table */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="quality-per-assignee">
        <div className="px-4 py-3 border-b border-neutral-100">
          <div className="text-[13px] font-semibold text-[#1a1a2e]">Quality breakdown by assignee</div>
          <div className="text-[11px] text-neutral-500 mt-0.5">Sorted by tasks completed (desc)</div>
        </div>
        {hasData && rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50">
                <tr className="text-left text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5">Assignee</th>
                  <th className="px-4 py-2.5 text-right">Completed</th>
                  <th className="px-4 py-2.5 text-right">Revisions</th>
                  <th className="px-4 py-2.5 min-w-[180px]">Revision rate</th>
                  <th className="px-4 py-2.5 min-w-[180px]">FTA rate</th>
                  <th className="px-4 py-2.5 text-right">Reopens</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.assignee_id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-2.5">
                      <div className="text-[13px] text-neutral-800">{r.name}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{r.completed}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-neutral-500">{r.revisions}</td>
                    <td className="px-4 py-2.5"><RateBar value={r.revision_rate} /></td>
                    <td className="px-4 py-2.5"><RateBar value={r.fta_rate} inverse /></td>
                    <td className={`px-4 py-2.5 text-right tabular-nums ${r.reopens > 0 ? 'text-red-600 font-medium' : 'text-neutral-500'}`}>
                      {r.reopens}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-10 text-center text-sm text-neutral-500">
            No completed tasks in this window. Widen the date range or clear filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default QualityTab;
